import { memo, type ReactNode } from 'react'
import { Dialog } from '@base-ui/react/dialog'
import { clsx } from 'clsx'

import css from './Modal.module.scss'

interface ModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  /** Accessible name for title-less dialogs. */
  ariaLabel?: string
  description?: ReactNode
  children?: ReactNode
  /** Buttons for the bottom row. */
  actions?: ReactNode
  /** Blocks Escape and backdrop dismissal while an action is in flight. */
  busy?: boolean
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

/**
 * One dialog shell for the whole app.
 *
 * Base UI owns focus trapping, focus restore, scroll locking, Escape, and the
 * aria wiring — all of which were previously hand-rolled and slightly
 * different in each of the three modals.
 */
function Modal({
  open,
  onOpenChange,
  title,
  ariaLabel,
  description,
  children,
  actions,
  busy = false,
  size = 'sm',
  className
}: ModalProps) {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(nextOpen) => {
        // Swallow Escape and backdrop clicks while an action is in flight.
        if (busy && !nextOpen) return
        onOpenChange(nextOpen)
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={css.backdrop} />
        <Dialog.Popup aria-label={ariaLabel} className={clsx('Modal', css.popup, css[size], className)}>
          {title && <Dialog.Title className={css.title}>{title}</Dialog.Title>}
          {(Boolean(description) || Boolean(children)) && (
            <div className={css.content}>
              {description && <Dialog.Description className={css.description}>{description}</Dialog.Description>}
              {children}
            </div>
          )}
          {actions && <div className={css.actions}>{actions}</div>}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export default memo(Modal)
